import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Выпадающий список в стиле панели «Курьеры». Нативный <select> нельзя стилизовать:
// macOS/iOS рисуют свой попап. Список рендерится порталом в body — карточки групп
// режут overflow. Значения — строки (как e.target.value у <select>), '' = не выбрано.
export default function CaSelect({
  value, options, placeholder = 'Выбрать', onChange, size = 'md', className = '', title, align = 'right',
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [hover, setHover] = useState(-1);
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const selected = options.find(o => String(o.value) === String(value ?? ''));

  useLayoutEffect(() => {
    if (!open) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(r.width, 220);
    const left = align === 'right' ? r.right - width : r.left;
    setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(left, window.innerWidth - width - 8)), width });
    setHover(options.findIndex(o => String(o.value) === String(value ?? '')));
  }, [open, align, options, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (listRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const pick = (val) => {
    onChange(val);
    setOpen(false);
    btnRef.current?.focus();
  };

  // Стрелки/Enter/Esc — свои, до глобальных хоткеев страницы (Enter = назначить)
  const onKey = (e) => {
    if (e.key === 'Escape') {
      if (open) { e.stopPropagation(); setOpen(false); }
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (!open) { setOpen(true); return; }
      const n = options.length;
      if (n) setHover(h => (h + (e.key === 'ArrowDown' ? 1 : -1) + n) % n);
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (!open) setOpen(true);
      else if (hover >= 0 && options[hover] && !options[hover].disabled) pick(options[hover].value);
    }
  };

  const isSel = (o) => selected && String(o.value) === String(selected.value);

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className={`ca-select ca-select-${size} ${selected ? 'chosen' : 'empty'} ${open ? 'open' : ''} ${className}`}
        onClick={() => setOpen(o => !o)}
        onKeyDown={onKey}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="ca-select-text">{selected ? selected.label : placeholder}</span>
        <span className="ca-select-chevron" aria-hidden="true" />
      </button>
      {open && pos && createPortal(
        <div ref={listRef} className="ca-select-list" style={{ top: pos.top, left: pos.left, width: pos.width }} role="listbox">
          {placeholder && (
            <div className={`ca-select-opt muted ${!selected ? 'sel' : ''}`} onClick={() => pick('')}>
              <span className="lbl">{placeholder}</span>
            </div>
          )}
          {options.map((o, i) => (
            <div
              key={String(o.value)}
              role="option"
              aria-selected={!!isSel(o)}
              className={`ca-select-opt ${isSel(o) ? 'sel' : ''} ${i === hover ? 'hover' : ''} ${o.disabled ? 'disabled' : ''}`}
              onMouseEnter={() => setHover(i)}
              onClick={() => { if (!o.disabled) pick(o.value); }}
            >
              <span className="lbl">{o.label}</span>
              {o.hint && <span className="hint">{o.hint}</span>}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
